import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const CurrencyCtx = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState("ARS");
  const [cotizacion, setCotizacion] = useState(() => {
    const stored = localStorage.getItem("erp_cotiz");
    return stored ? parseFloat(stored) : 1050;
  });
  // Metadata sobre la última actualización desde dolarhoy.com
  const [cotizMeta, setCotizMeta] = useState(() => {
    try {
      const raw = localStorage.getItem("erp_cotiz_meta");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [cotizLoading, setCotizLoading] = useState(false);
  // Si el usuario editó manualmente el valor, no se pisa con auto-refresh
  const [cotizManual, setCotizManual] = useState(() => localStorage.getItem("erp_cotiz_manual") === "1");

  const updateCotiz = (v) => {
    setCotizacion(v);
    localStorage.setItem("erp_cotiz", String(v));
    // Al editar manualmente se marca el flag; el auto-refresh no lo pisará
    setCotizManual(true);
    localStorage.setItem("erp_cotiz_manual", "1");
  };

  // Trae la cotización oficial venta desde el backend (que scrapea dolarhoy.com)
  const refreshCotizacion = useCallback(async ({ force = false, overrideManual = false } = {}) => {
    setCotizLoading(true);
    try {
      const { data } = await api.get("/cotizacion/dolar", { params: force ? { force: true } : {} });
      const meta = {
        source: data.source,
        source_url: data.source_url,
        tipo: data.tipo,
        compra: data.compra,
        venta: data.venta,
        updated_at: data.updated_at,
        cached: !!data.cached,
        stale: !!data.stale,
      };
      setCotizMeta(meta);
      localStorage.setItem("erp_cotiz_meta", JSON.stringify(meta));
      // Solo pisar el valor local si el usuario no lo editó manualmente
      // (o si explícitamente pidió sincronizar)
      if (overrideManual || !cotizManual) {
        setCotizacion(data.venta);
        localStorage.setItem("erp_cotiz", String(data.venta));
        setCotizManual(false);
        localStorage.setItem("erp_cotiz_manual", "0");
      }
      return meta;
    } catch (e) {
      // silencioso — la UI muestra que no se pudo actualizar
      return null;
    } finally {
      setCotizLoading(false);
    }
  }, [cotizManual]);

  // Auto-fetch al montar (una vez)
  useEffect(() => {
    refreshCotizacion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CurrencyCtx.Provider value={{
      currency, setCurrency,
      cotizacion, setCotizacion: updateCotiz,
      cotizMeta, cotizLoading, cotizManual,
      refreshCotizacion,
    }}>
      {children}
    </CurrencyCtx.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyCtx);
